import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * refresh-parent-portal-ui 0.1: initials on a pastel tone picked
 * deterministically from the name (hash mod 6). Real photos are out of scope.
 */
@Component({
  selector: 'ui-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="avatar"
      [attr.data-testid]="'avatar-' + initials()"
      [style.width.px]="size()"
      [style.height.px]="size()"
    >{{ initials() }}</span>
  `,
  styles: [
    `
      .avatar {
        display: grid;
        place-items: center;
        border-radius: 50%;
        font-weight: 900;
        font-size: 18px;
        border: 2px solid #fff;
        background: var(--av-bg);
        color: var(--av-ink);
        flex: 0 0 auto;
      }
    `,
  ],
  host: {
    '[style.--av-bg]': 'tones().bg',
    '[style.--av-ink]': 'tones().ink',
  },
})
export class UiAvatarComponent {
  readonly name = input.required<string>();
  readonly size = input(48);

  private static readonly TONES = [
    { bg: 'var(--gp-tile-sage-bg)', ink: 'var(--gp-tile-sage-ink)' },
    { bg: 'var(--gp-tile-mint-bg)', ink: 'var(--gp-tile-mint-ink)' },
    { bg: 'var(--gp-tile-sky-bg)', ink: 'var(--gp-tile-sky-ink)' },
    { bg: 'var(--gp-tile-butter-bg)', ink: 'var(--gp-tile-butter-ink)' },
    { bg: 'var(--gp-tile-peach-bg)', ink: 'var(--gp-tile-peach-ink)' },
    { bg: 'var(--gp-tile-lavender-bg)', ink: 'var(--gp-tile-lavender-ink)' },
  ];

  /* exported for the spec */
  static initialsOf(name: string): string {
    return (name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?';
  }

  static toneIndexFor(name: string): number {
    let h = 0;
    for (const ch of name || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return h % 6;
  }

  readonly initials = computed(() => UiAvatarComponent.initialsOf(this.name()));
  readonly tones = computed(() => UiAvatarComponent.TONES[UiAvatarComponent.toneIndexFor(this.name())]);
}